import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Form, Input, Label, Row, Spinner, Alert } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { get } from "helpers/api_helper";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import { GET_VENDOR_BY_ID } from "constants/api_constants";
import { showErrorToast } from "helpers/toastMessages";

function ViewVendor() {
  const [vendorData, setVendorData] = useState({
    company: "",
    vatNumber: "",
    website: "",
    phone: "",
    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    primaryContactDesignation: "",
    paymentTerms: "",
    currency: "",
    country: "",
    province: "",
    zipCode: "",
    city: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [missingFields, setMissingFields] = useState([]);

  const { vendorId } = useParams();
  console.log("vendorId",vendorId);
  
  const navigate = useNavigate();

  useEffect(() => {
    if (vendorId) {
      fetchVendorData();
    } else {
      setApiError("Vendor ID is required");
      showErrorToast("Vendor ID is required");
    }
  }, [vendorId]);

  const fetchVendorData = async () => {
    setIsLoading(true);
    setApiError("");
    setMissingFields([]);
    try {
      const response = await get(GET_VENDOR_BY_ID(vendorId));
      console.log("response",response);
      
      const vendData = response?.data?.data?.[0] 
      if (vendData) {
        const missing = [];
        const checkField = (field, name) => {
          if (!vendData[field]) missing.push(name);
          return vendData[field] || "";
        };

        setVendorData({
          company: checkField("companyName", "Company Name"),
          vatNumber: checkField("vat", "VAT / GST"),
          website: checkField("website", "Website"),
          phone: checkField("phone", "Phone"),
          primaryContactName: checkField("primaryContactName", "Primary Contact Name"),
          primaryContactEmail: checkField("email", "Primary Contact Email"),
          primaryContactPhone: checkField("primaryPhone", "Primary Contact Phone"),
          primaryContactDesignation: checkField("designation", "Designation"),
          paymentTerms: vendData.paymentTerms || "",
          currency: checkField("currency", "Currency"),
          country: checkField("country", "Country") || checkField("state", "Country (State)"),
          province: checkField("province", "Province") || checkField("state", "Province (State)"),
          zipCode: checkField("zipCode", "Zip Code") || checkField("zipcode", "Zip Code"),
          city: checkField("city", "City"),
          address: checkField("address", "Address"),
        });

        if (missing.length > 0) {
          setMissingFields(missing);
          showErrorToast(`Missing fields: ${missing.join(", ")}`);
        }
      } else {
        setApiError("No vendor data found");
        showErrorToast("No vendor data found");
      }
    } catch (error) {
      const errorMsg = error.message || "Failed to fetch vendor data";
      setApiError(errorMsg);
      showErrorToast(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading="View Vendor"
          routePath="Home"
          pageName="Vendor Details"
        />
        <Row className="p-4">
          <Col>
            <Card>
              <CardBody>
                {isLoading && (
                  <div className="text-center my-3">
                    <Spinner color="primary" />
                  </div>
                )}
                {apiError && (
                  <Alert color="danger" className="text-center my-3">
                    {apiError}
                    <button onClick={fetchVendorData} className="btn btn-link">
                      Retry
                    </button>
                  </Alert>
                )}
                {!isLoading && !apiError && (
                  <Form className="row g-3 py-2">
                    <Row className="card-group">
                      <Col xs={12} className="card-header">
                        <p className="form-header">Vendor Information</p>
                      </Col>
                    </Row>
                    <Row className="justify-content-start">
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Company Name</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.company || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">VAT / GST</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.vatNumber || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Website</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.website || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Phone</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.phone || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Primary Contact Name</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.primaryContactName || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Primary Contact Email</Label>
                        <Input
                          type="email"
                          className="form-control"
                          value={vendorData.primaryContactEmail || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Primary Contact Phone</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.primaryContactPhone || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Designation</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.primaryContactDesignation || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Country</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.country || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Currency</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.currency || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Payment Terms (Days)</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.paymentTerms || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Province</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.province || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">City</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.city || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Zip Code</Label>
                        <Input
                          type="text"
                          className="form-control"
                          value={vendorData.zipCode || "-"}
                          disabled
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Label className="col-form-label">Address</Label>
                        <Input
                          type="textarea"
                          className="form-control"
                          value={vendorData.address || "-"}
                          disabled
                          rows="3"
                        />
                      </Col>
                    </Row>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Footer
          saveText="View Billing and Shipping Information"
          save={() => navigate(`/view-vendor-shipping-billing/${vendorId}`)}
          buttons={[
            {
              text: "Edit Vendor",
              color: "primary",
              onClick: () => navigate(`/add-vendor/${vendorId}`),
            },
          ]}
          showBackConfirmation={false}
        />
      </Container>
    </div>
  );
}

export default ViewVendor;